import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { Users, Car, Building2, Handshake, ShieldCheck, XCircle, CheckCircle2, RefreshCw, FileText, Sparkles, AlertTriangle, Trash2, ExternalLink } from "lucide-react";

const TABS = [
  { key: "drivers", icon: Users },
  { key: "vehicles", icon: Car },
  { key: "fleets", icon: Building2 },
  { key: "partners", icon: Handshake },
];

const ENDPOINTS = {
  drivers: "/admin/approvals/drivers",
  vehicles: "/admin/approvals/vehicles",
  fleets: "/admin/approvals/fleets",
  partners: "/admin/approvals/partners",
};

export default function Approvals() {
  const { t } = useLang();
  const [queue, setQueue] = useState(null);
  const [tab, setTab] = useState("drivers");
  const [loading, setLoading] = useState(false);
  const [rejectionOpen, setRejectionOpen] = useState(null);
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/approvals/queue");
      setQueue(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    drivers: queue?.drivers?.length || 0,
    vehicles: queue?.vehicles?.length || 0,
    fleets: queue?.fleets?.length || 0,
    partners: queue?.partners?.length || 0,
  }), [queue]);

  const items = queue?.[tab] || [];

  const approve = async (item) => {
    try {
      await api.post(`${ENDPOINTS[tab]}/${item.id}`, { action: "approve" });
      toast.success(t("status_approved"));
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reject = async () => {
    if (!rejectionOpen) return;
    try {
      await api.post(`${ENDPOINTS[tab]}/${rejectionOpen.id}`, { action: "reject", reason });
      toast.success(t("status_rejected"));
      setRejectionOpen(null);
      setReason("");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-10">
      <PageHeader
        eyebrow="Onboarding"
        title={t("nav_approvals")}
        description={t("approvals_description")}
      >
        <GhostButton onClick={load} disabled={loading} data-testid="reload-queue">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t("action_reload")}
        </GhostButton>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tk) => {
          const Icon = tk.icon;
          const active = tab === tk.key;
          const n = counts[tk.key];
          return (
            <button
              key={tk.key}
              onClick={() => { setTab(tk.key); setSelected(null); }}
              data-testid={`tab-${tk.key}`}
              className={`inline-flex items-center gap-2 h-10 px-4 border text-xs uppercase tracking-wide transition-colors ${
                active ? "border-[#f59e0b] text-[#0a0f1e] bg-[#f59e0b]/10 font-semibold" : "border-slate-200 text-slate-600 hover:border-[#f59e0b]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t(`nav_${tk.key}`)}
              <span className={`ml-1 px-1.5 py-0.5 text-[10px] ${n > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 border border-slate-200 bg-white">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold">{t("status_pending")} ({items.length})</span>
          </div>
          <div className="max-h-[600px] overflow-y-auto divide-y divide-slate-100" data-testid="queue-list">
            {items.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">{t("status_pending")} · 0</div>}
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelected(item)}
                data-testid={`queue-item-${item.id}`}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 ${selected?.id === item.id ? "bg-[#f59e0b]/10 border-l-2 border-[#f59e0b]" : ""}`}
              >
                <div className="font-medium text-[#0a0f1e] truncate">{item.name || item.company_name || item.plate || `${item.brand} ${item.model}`}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                  {item.fleet_owned && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase text-amber-700 border border-amber-300 bg-amber-50 px-1.5 py-0.5">
                      <Building2 className="w-2.5 h-2.5" /> Fleet
                    </span>
                  )}
                  {!item.fleet_owned && tab === "drivers" && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase text-slate-600 border border-slate-300 bg-slate-50 px-1.5 py-0.5">
                      <ShieldCheck className="w-2.5 h-2.5" /> Independent
                    </span>
                  )}
                  {item.fleet_name && <span className="text-slate-400 truncate">· {item.fleet_name}</span>}
                  {item.email && !item.fleet_name && <span className="text-slate-400 truncate">{item.email}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2 border border-slate-200 bg-white min-h-[600px]">
          {selected ? (
            <DetailPanel
              tab={tab}
              item={selected}
              onApprove={() => approve(selected)}
              onReject={() => { setRejectionOpen(selected); setReason(""); }}
              t={t}
            />
          ) : (
            <div className="h-full flex items-center justify-center p-10 text-center text-slate-400 text-sm">
              <div>
                <FileText className="w-8 h-8 mx-auto mb-3" />
                Selecione um item da fila para ver os documentos e detalhes.
              </div>
            </div>
          )}
        </div>
      </div>

      {rejectionOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
          <div className="bg-white border border-slate-200 shadow-xl max-w-md w-full">
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="text-sm font-semibold text-[#0a0f1e]">{t("reject_prefix")}: {rejectionOpen.name || rejectionOpen.company_name || rejectionOpen.plate}</div>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">{t("rejection_reason_label")}</div>
              <textarea
                data-testid="rejection-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("rejection_reason_placeholder")}
                rows={4}
                className="w-full border border-slate-300 focus:border-[#f59e0b] focus:outline-none px-3 py-2 text-sm"
              />
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex gap-3 justify-end">
              <GhostButton onClick={() => { setRejectionOpen(null); setReason(""); }} data-testid="cancel-reject">{t("cancel")}</GhostButton>
              <button onClick={reject} data-testid="confirm-reject" className="h-10 px-5 bg-red-600 text-white text-xs uppercase tracking-wide font-semibold hover:bg-red-700">
                <XCircle className="w-3.5 h-3.5 inline mr-2" /> {t("reject")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailPanel({ tab, item, onApprove, onReject, t }) {
  const detailPath = {
    drivers: `/admin/drivers/${item.id}`,
    vehicles: `/admin/vehicles?edit=${item.id}`,
    fleets: `/admin/fleets/${item.id}`,
    partners: `/admin/partners/${item.id}`,
  }[tab];
  return (
    <div>
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-lg font-serif text-[#0a0f1e]" data-testid="detail-title">
            {item.name || item.company_name || `${item.brand} ${item.model}`}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
            <StatBadge status={item.status} />
            {item.fleet_owned && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase text-amber-700 border border-amber-300 bg-amber-50 px-1.5 py-0.5">
                <Building2 className="w-2.5 h-2.5" /> {t("fleet_owned_badge")}
              </span>
            )}
            {!item.fleet_owned && tab === "drivers" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase text-slate-600 border border-slate-300 bg-slate-50 px-1.5 py-0.5">
                <ShieldCheck className="w-2.5 h-2.5" /> {t("independent_badge")}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <a href={detailPath} target="_blank" rel="noreferrer" className="h-10 px-3 border border-slate-300 hover:border-[#f59e0b] text-slate-700 hover:text-[#f59e0b] text-xs uppercase tracking-wide font-semibold inline-flex items-center gap-1.5" data-testid="open-full-record">
            <ExternalLink className="w-3.5 h-3.5" /> {t("profile_full_record")}
          </a>
          <button onClick={onReject} data-testid="reject-btn" className="h-10 px-4 border border-red-300 text-red-600 text-xs uppercase tracking-wide font-semibold hover:bg-red-50">
            <XCircle className="w-3.5 h-3.5 inline mr-2" /> {t("reject")}
          </button>
          <GoldButton onClick={onApprove} data-testid="approve-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> {t("approve")}
          </GoldButton>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
        {tab === "drivers" && <DriverFields item={item} />}
        {tab === "vehicles" && <VehicleFields item={item} />}
        {tab === "fleets" && <FleetFields item={item} />}
        {tab === "partners" && <PartnerFields item={item} />}
      </div>

      <DocsSection tab={tab} item={item} />
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-800 text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}

function DriverFields({ item }) {
  return (
    <>
      <KV label="Email" value={item.email} />
      <KV label="Telefone" value={item.phone} />
      <KV label="WhatsApp" value={item.whatsapp || item.phone} />
      <KV label="NIF" value={item.nif} />
      <KV label="Base" value={item.base_location} />
      <KV label="Raio de trabalho (km)" value={item.working_radius_km || item.max_distance_km} />
      <KV label="Zonas" value={(item.zones_covered || []).join(", ")} />
      <KV label="Viagens longas" value={item.long_trip_available ? "Sim" : "Não"} />
      <KV label="Idiomas" value={(item.languages || []).join(", ")} />
      <KV label="Interesses" value={item.hobbies} />
      <KV label="Preço / km (€)" value={item.pricing_km} />
      <KV label="Preço / hora (€)" value={item.pricing_hour} />
    </>
  );
}

function VehicleFields({ item }) {
  return (
    <>
      <KV label="Matrícula" value={item.plate} />
      <KV label="Marca / Modelo" value={`${item.brand} ${item.model}`} />
      <KV label="Ano" value={item.year} />
      <KV label="Categoria" value={item.type} />
      <KV label="Capacidade" value={`${item.capacity_passengers} pax · ${item.capacity_luggage} malas`} />
      <KV label="Frota" value={item.fleet_name || "—"} />
      <KV label="RNAAT" value={item.rnaat_number} />
      <KV label="Validade seguro" value={item.insurance_expiry || item.doc_green_card_expiry} />
      <KV label="Validade IPO" value={item.inspection_expiry || item.doc_inspection_expiry} />
      <KV label="Validade DUA" value={item.doc_dua_expiry} />
    </>
  );
}

function FleetFields({ item }) {
  return (
    <>
      <KV label="NIF" value={item.nif} />
      <KV label="Representante legal" value={item.legal_representative} />
      <KV label="Email" value={item.email} />
      <KV label="Telefone" value={item.phone} />
      <KV label="Morada" value={item.address} />
      <KV label="IBAN" value={item.iban} />
      <KV label="Sócios" value={(item.partners_ids || []).map((p) => p.name).join(", ")} />
    </>
  );
}

function PartnerFields({ item }) {
  return (
    <>
      <KV label="Tipo" value={item.type} />
      <KV label="NIF" value={item.nif} />
      <KV label="Contacto" value={item.contact_person} />
      <KV label="Email" value={item.email} />
      <KV label="Telefone" value={item.phone} />
      <KV label="Morada" value={item.address} />
      <KV label="Slug" value={item.slug} />
      <KV label="Reservas mensais estimadas" value={item.expected_monthly_bookings} />
    </>
  );
}

function DocsSection({ tab, item }) {
  const [busy, setBusy] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const docs = [];
  const docsMeta = item.docs_meta || {};

  if (tab === "drivers") {
    docs.push(["Carta condução", "doc_driving_license", item.doc_driving_license, item.doc_driving_license_expiry]);
    docs.push([item.doc_id_type === "passport" ? "Passaporte" : "CC", "doc_id_or_passport", item.doc_id_or_passport]);
    if (item.doc_id_type === "passport") docs.push(["Aut. residência", "doc_residency_permit", item.doc_residency_permit]);
    docs.push(["Registo criminal", "doc_criminal_record", item.doc_criminal_record, item.doc_criminal_record_expiry]);
    docs.push(["Comprovativo morada", "doc_address_proof", item.doc_address_proof]);
  } else if (tab === "vehicles") {
    docs.push(["DUA", "doc_dua", item.doc_dua, item.doc_dua_expiry]);
    docs.push(["Carta verde / Seguro", "doc_green_card", item.doc_green_card, item.doc_green_card_expiry]);
    docs.push(["IPO", "doc_inspection", item.doc_inspection, item.doc_inspection_expiry]);
    (item.photos_exterior || []).slice(0, 1).forEach((p, i) => docs.push([`Foto exterior`, `photos_exterior_${i}`, p]));
    (item.photos_interior || []).forEach((p, i) => docs.push([`Foto interior ${i === 0 ? "frente" : "trás"}`, `photos_interior_${i}`, p]));
    if (item.photo_trunk) docs.push(["Foto bagageira", "photo_trunk", item.photo_trunk]);
  } else if (tab === "fleets") {
    docs.push(["Certidão comercial", "doc_commercial_certificate", item.doc_commercial_certificate]);
    docs.push(["Contrato assinado", "doc_signed_contract", item.doc_signed_contract]);
  }

  const present = docs.filter(([, , url]) => !!url);
  if (present.length === 0) return null;

  const entityType = tab.slice(0, -1); // drivers -> driver

  const reanalyze = async (field) => {
    setBusy(`reanalyze-${field}`);
    try {
      await api.post("/admin/documents/reanalyze", { entity_type: entityType, entity_id: item.id, field });
      toast.success("Documento reanalisado pela IA");
      setRefresh(refresh + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const softDelete = async (field) => {
    const reason = window.prompt("Motivo da remoção (obrigatório):");
    if (!reason || reason.length < 3) return;
    setBusy(`del-${field}`);
    try {
      await api.post("/admin/documents/soft-delete", { entity_type: entityType, entity_id: item.id, field, reason });
      toast.success("Documento removido (soft delete). Registo em audit_log.");
      setRefresh(refresh + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const reviewDoc = async (field, decision) => {
    const reason = decision === "reject" ? window.prompt("Motivo (obrigatório para rejeição):") : null;
    if (decision === "reject" && (!reason || reason.length < 3)) return;
    setBusy(`review-${field}`);
    try {
      await api.post("/admin/documents/review", { entity_type: entityType, entity_id: item.id, field, decision, reason });
      toast.success(decision === "approve" ? "Documento aprovado" : "Documento rejeitado");
      setRefresh(refresh + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="border-t border-slate-200 mt-2">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">Documentos ({present.length})</span>
        <span className="text-[10px] text-slate-500">IA · Gemini 3.1 Pro</span>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {present.map(([label, field, url, expiry], i) => {
          const meta = docsMeta[field] || {};
          const isManual = meta.ai_status === "needs_manual";
          const isAnalyzed = meta.ai_status === "analyzed";
          const isDeleted = !!meta.deleted_at;
          const approval = meta.approval_status;
          return (
            <div key={i} className={`border p-3 text-sm ${isDeleted ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200 hover:border-[#f59e0b]"} transition-colors`} data-testid={`doc-${field}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-slate-900 font-semibold">{label}</div>
                  {expiry && <div className="text-[10px] uppercase text-slate-600 mt-1 font-semibold">Validade: {expiry}</div>}
                </div>
                <div className="flex items-center gap-1">
                  {isManual && (
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase text-amber-800 bg-amber-100 px-1.5 py-0.5 font-bold" data-testid={`ai-manual-${field}`}>
                      <AlertTriangle className="w-2.5 h-2.5" /> Manual
                    </span>
                  )}
                  {isAnalyzed && (
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase text-emerald-800 bg-emerald-100 px-1.5 py-0.5 font-bold" data-testid={`ai-analyzed-${field}`}>
                      <Sparkles className="w-2.5 h-2.5" /> IA
                    </span>
                  )}
                </div>
              </div>
              {meta.ai_confidence != null && (
                <div className="text-[10px] text-slate-600 mt-1">Confiança IA: <b>{(meta.ai_confidence * 100).toFixed(0)}%</b></div>
              )}
              {meta.ai_issues && meta.ai_issues.length > 0 && (
                <div className="text-[10px] text-red-700 mt-1 line-clamp-2" title={meta.ai_issues.join("; ")}>⚠ {meta.ai_issues[0]}</div>
              )}
              {approval && approval !== "auto_approved" && (
                <div className="text-[10px] text-slate-700 mt-1 uppercase tracking-wider">Aprovação: <b>{approval}</b></div>
              )}
              {isDeleted && (
                <div className="text-[10px] text-red-700 mt-1">Removido em {meta.deleted_at?.slice(0,10)} — motivo: {meta.deleted_reason}</div>
              )}
              <div className="flex items-center gap-1 mt-3">
                <a href={url} target="_blank" rel="noreferrer" className="h-7 px-2 border border-slate-300 hover:border-[#f59e0b] text-[10px] uppercase tracking-widest flex items-center gap-1 text-slate-800 font-semibold">
                  <FileText className="w-3 h-3" /> Ver
                </a>
                <button disabled={busy === `reanalyze-${field}`} onClick={() => reanalyze(field)} className="h-7 px-2 border border-slate-300 hover:border-[#f59e0b] text-[10px] uppercase tracking-widest flex items-center gap-1 text-slate-800 font-semibold disabled:opacity-50" data-testid={`reanalyze-${field}`}>
                  <RefreshCw className={`w-3 h-3 ${busy === `reanalyze-${field}` ? "animate-spin" : ""}`} /> IA
                </button>
                {approval !== "approved" && !isDeleted && (
                  <button disabled={busy === `review-${field}`} onClick={() => reviewDoc(field, "approve")} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] uppercase tracking-widest flex items-center gap-1 font-semibold" data-testid={`approve-doc-${field}`}>
                    <CheckCircle2 className="w-3 h-3" /> OK
                  </button>
                )}
                {approval !== "rejected" && !isDeleted && (
                  <button disabled={busy === `review-${field}`} onClick={() => reviewDoc(field, "reject")} className="h-7 px-2 border border-red-300 text-red-700 hover:bg-red-50 text-[10px] uppercase tracking-widest flex items-center gap-1 font-semibold" data-testid={`reject-doc-${field}`}>
                    <XCircle className="w-3 h-3" /> Rej
                  </button>
                )}
                {!isDeleted && (
                  <button disabled={busy === `del-${field}`} onClick={() => softDelete(field)} className="h-7 px-2 border border-slate-300 hover:border-red-400 text-slate-600 hover:text-red-600 text-[10px] uppercase tracking-widest flex items-center gap-1 font-semibold ml-auto" data-testid={`soft-delete-${field}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
